/**
 * {@link AuthApi} over the real worker.
 *
 * The only module in the portal that knows what the auth endpoints are called.
 * Everything above it depends on the interface, which is what lets a component
 * test hand a form a stub and lets the flow tests replace HTTP wholesale.
 */

import { API_BASE_URL } from "../api/apiBase";
import { AuthError, type AuthApi, type SessionUser } from "./authApi";

/**
 * Every request carries the session cookie, and could not work without it: the
 * cookie is `HttpOnly`, so this code cannot read or attach it by hand — the
 * browser does, but only when asked. `same-origin` (the default) would send
 * nothing, because the portal and the API are different subdomains.
 */
const CREDENTIALS: RequestCredentials = "include";

export function createHttpAuthApi(baseUrl: string = API_BASE_URL): AuthApi {
  async function send(path: string, body: unknown): Promise<unknown> {
    const response = await fetch(`${baseUrl}${path}`, {
      method: "POST",
      credentials: CREDENTIALS,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return unwrap(response);
  }

  return {
    async register({ email, password }) {
      // Better Auth requires a display name and the form does not collect one;
      // the worker fills it in from the address (see `apps/api/src/auth.ts`).
      // Sending the empty string is what makes that hook fire.
      await send("/v1/auth/sign-up/email", { name: "", email, password });
    },

    async signIn({ email, password }) {
      await send("/v1/auth/sign-in/email", { email, password });
    },

    async signOut() {
      await send("/v1/auth/sign-out", {});
    },

    async requestPasswordReset(email) {
      // Deliberately no `redirectTo`. Better Auth accepts one, validates it
      // against its trusted origins — and then never uses it here, because
      // `sendResetPassword` composes the link from the worker's own
      // `portalOrigin`. Sending it therefore bought nothing and cost a failure
      // mode: from any other origin, such as a Cloudflare Pages preview
      // deployment, the origin check refuses the whole request with
      // INVALID_REDIRECT_URL and password reset stops working there.
      await send("/v1/auth/request-password-reset", { email });
    },

    async resetPassword({ token, password }) {
      await send("/v1/auth/reset-password", { token, newPassword: password });
    },

    async changePassword({ currentPassword, newPassword }) {
      await send("/v1/auth/change-password", {
        currentPassword,
        newPassword,
        // Not offered as a choice. Someone changing their password is often
        // doing it *because* they think another session is not theirs, and a
        // checkbox they have to find is a checkbox they will miss. The session
        // doing the changing is kept, so this signs out other devices, not
        // this one.
        revokeOtherSessions: true,
      });
    },

    async verifyEmail(token) {
      // A GET, because it is reached by clicking a link in an email client.
      const response = await fetch(
        `${baseUrl}/v1/auth/verify-email?token=${encodeURIComponent(token)}`,
        { credentials: CREDENTIALS },
      );
      await unwrap(response);
    },

    async me() {
      const response = await fetch(`${baseUrl}/v1/me`, { credentials: CREDENTIALS });

      // Not signed in is an answer, not a failure. Rejecting here would make
      // every caller wrap the ordinary case in a try/catch.
      if (response.status === 401) return null;

      const body = (await unwrap(response)) as { user: SessionUser };
      return body.user;
    },
  };
}

/**
 * Turns a response into either its body or an {@link AuthError} carrying what
 * the worker said. Better Auth answers with `{ message, code }`; the portal's
 * own handlers answer with `{ error, message }`. Both are read, because a page
 * that branches on `code` must not silently stop branching the day a request
 * happens to be refused by the other half of the API.
 */
async function unwrap(response: Response): Promise<unknown> {
  const body = await response.json().catch(() => null);

  if (response.ok) return body;

  const detail = body as { message?: string; code?: string; error?: string } | null;
  throw new AuthError(
    detail?.message ?? "Something went wrong. Please try again.",
    detail?.code ?? detail?.error,
  );
}
